import { Assert } from "../../../../Assert";
import { Constructor } from "../../../../Extensions";
import { IRadObject, RadObject, RadObjectConstructor } from "../../../../RadObject";
import { ServiceManager } from "../../../../ServiceManager";
import { WebApiClientService } from "../../../WebApiClientService";
import { IAttribute } from "../../System.Runtime/Attribute";
import { Class } from "../../System/Class";
import { TypeInitializerObjectVisitor } from "../../TypeInitializerObjectVisitor";
import { NAMESPACE } from "./_Namespace";
import { IEnumValue } from "./EnumValue";
import { ITypeInfoMetadata } from "./ITypeInfoMetadata";
import { IPropertyInfo, PropertyInfo } from "./PropertyInfo";

export type TypeInfoPropertyValues =
    {
        attributes?: IAttribute[];
        baseType?: ITypeInfo;
        classHierarchy?: string[];
        enumValues?: { [name: string]: IEnumValue };
        isEnum?: boolean;
        name: string;
        properties: { [key: string]: IPropertyInfo };
        underlyingTypeId: string;
    };

export type TypeInfoProperties = {
    [name in keyof TypeInfoPropertyValues]?: TypeInfoPropertyValues[name];
};

export interface ITypeInfo extends TypeInfoProperties
{
    attributes: IAttribute[];
    baseType?: ITypeInfo;
    classHierarchy?: string[],
    name: string;
    properties: { [key: string]: IPropertyInfo };
    underlyingTypeId?: string;

    getAttribute<T extends IAttribute>(attribute: Constructor<T>): T | undefined;
    isAssignableTo(underlyingTypeId: string): boolean;
}

export class TypeInfo extends Class implements ITypeInfo
{
    // #region Static

    public static override classNamespace = NAMESPACE + "TypeInfo";
    public static override className = "TypeInfo";

    // #region Global Variables

    private static _typeInfoCache: { [key: string]: Promise<ITypeInfo> | undefined } = {};
    private static _typeInfoMetadataCache: { [key: string]: Promise<ITypeInfoMetadata> | undefined } = {};
    private static _typeInitializerObjectVisitor = new TypeInitializerObjectVisitor();

    // #endregion

    // #region Public Methods

    public static async loadTypeInfoFromServerAsync(typeId: string): Promise<ITypeInfo>
    {
        return this.fromTypeInfoMetadataAsync(await this.loadTypeInfoMetadataFromServerAsync(typeId));
    }
    public static async loadTypeInfoMetadataFromServerAsync(typeId: string): Promise<ITypeInfoMetadata>
    {
        Assert.isNotNullOrUndefined(typeId, "typeId");

        this._typeInfoMetadataCache[typeId] ??=
            ServiceManager.getRequired(WebApiClientService)
                .createApiMethodRequest("Metadata", "GetTypeInfoMetadata", { typeId: typeId })
                .executeAsync() as Promise<ITypeInfoMetadata>;

        return this._typeInfoMetadataCache[typeId];
    }
    public static forRadObject(radObject: IRadObject): TypeInfo
    {
        const
            constructor = radObject.constructor as RadObjectConstructor,
            prototype = Object.getPrototypeOf(radObject),
            typeInfo = new TypeInfo({
                attributes: constructor.attributes,
                baseType: prototype ? this.forRadObject(prototype) : undefined,
                name: constructor.classFullName ?? constructor.name,
                properties:
                    RadObject
                        .getProperties(radObject)
                        .map(item => PropertyInfo.forProperty(item.propertyName, item.property))
                        .reduce((obj, pi) => ({ ...obj, [pi.name!]: pi }), {}),
                underlyingTypeId: constructor.classFullName ?? constructor.name,
            });

        return typeInfo;
    }
    public static async fromTypeInfoMetadataAsync(typeInfoMetadata: ITypeInfoMetadata): Promise<ITypeInfo>
    {
        Assert.isNotNullOrUndefined(typeInfoMetadata.underlyingTypeId, "typeInfoMetadata.underlyingTypeId");

        this._typeInfoCache[typeInfoMetadata.underlyingTypeId] ??=
            this._typeInitializerObjectVisitor.visitAsync<TypeInfo>(typeInfoMetadata)
                .then((typeInfo) =>
                {
                    Assert.isNotNullOrUndefined(typeInfo, `Unable to create type info for metadata \"${typeInfoMetadata.$type}\"`);
                    return typeInfo;
                });

        return this._typeInfoCache[typeInfoMetadata.underlyingTypeId] as Promise<ITypeInfo>;
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public attributes: IAttribute[];
    public baseType?: ITypeInfo;
    public classHierarchy?: string[];
    public enumValues?: { [name: string]: IEnumValue };
    public isEnum?: boolean;
    public name: string;
    public properties: { [key: string]: IPropertyInfo };
    public underlyingTypeId?: string;

    // #endregion

    // #region Constructor

    constructor(propertyValues: Partial<TypeInfoPropertyValues>)
    {
        super();

        if (!propertyValues.name)
            throw new Error("name is required");

        this.attributes = propertyValues.attributes ?? [];
        this.baseType = propertyValues.baseType;
        this.classHierarchy = propertyValues.classHierarchy;
        this.enumValues = propertyValues.enumValues;
        this.isEnum = propertyValues.isEnum;
        this.name = propertyValues.name;
        this.properties = propertyValues.properties ?? {};
        this.underlyingTypeId = propertyValues.underlyingTypeId;

        for (const propertyKey in this.properties)
            this.properties[propertyKey].typeInfo = this;
    }

    // #endregion

    // #region Public Methods

    public getAttribute<T extends IAttribute>(attribute: Constructor<T>): T | undefined
    {
        return this.attributes?.find(item => item instanceof attribute) as T;
    }
    public isAssignableTo(underlyingTypeId: string): boolean
    {
        let
            typeInfo: ITypeInfo | undefined = this;

        while (typeInfo)
        {
            if (typeInfo.underlyingTypeId === underlyingTypeId)
                return true;

            typeInfo = typeInfo.baseType;
        }

        return false;
    }

    // #endregion

    // #endregion
}